const PBKDF2_ITERATIONS = 150000;
const AES_KEY_LENGTH = 256;
const DEK_BYTES = 32;

const enc = new TextEncoder();
const dec = new TextDecoder();

const toBase64 = (bytes: Uint8Array): string => {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

const fromBase64 = (value: string): Uint8Array => {
  const binary = atob(value);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    out[i] = binary.charCodeAt(i);
  }
  return out;
};

const toBase64Url = (bytes: Uint8Array): string =>
  toBase64(bytes).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');

const fromBase64Url = (value: string): Uint8Array => {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/');
  const padLen = (4 - (padded.length % 4)) % 4;
  return fromBase64(padded + '='.repeat(padLen));
};

const randomBytes = (length: number): Uint8Array => {
  const out = new Uint8Array(length);
  crypto.getRandomValues(out);
  return out;
};

const toArrayBuffer = (bytes: Uint8Array): ArrayBuffer =>
  bytes.buffer.slice(
    bytes.byteOffset,
    bytes.byteOffset + bytes.byteLength
  ) as ArrayBuffer;

const deriveAesKey = async (key: string, salt: Uint8Array): Promise<CryptoKey> => {
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    enc.encode(key),
    'PBKDF2',
    false,
    ['deriveKey']
  );

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: toArrayBuffer(salt),
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    keyMaterial,
    {
      name: 'AES-GCM',
      length: AES_KEY_LENGTH,
    },
    false,
    ['encrypt', 'decrypt']
  );
};

const importRawAesKey = async (dek: Uint8Array): Promise<CryptoKey> => {
  return crypto.subtle.importKey(
    'raw',
    toArrayBuffer(dek),
    { name: 'AES-GCM', length: AES_KEY_LENGTH },
    false,
    ['encrypt', 'decrypt']
  );
};

interface EncPayloadV1 {
  v: 1;
  s: string;
  i: string;
  c: string;
}

interface EncAudioPayloadV1 {
  v: 1;
  s: string;
  i: string;
  m: string;
}

interface EncPayloadV3 {
  v: 3;
  i: string;
  c: string;
}

interface EncAudioPayloadV3 {
  v: 3;
  i: string;
  m: string;
}

/** Generate a 32-byte DEK encoded as base64url (transport / vault form). */
export const generateMessageDek = (): string => toBase64Url(randomBytes(DEK_BYTES));

const parseDek = (key: string): Uint8Array | null => {
  const trimmed = key.trim();
  if (!trimmed) return null;
  try {
    // enc3 vault keys are base64url of 32 raw bytes
    const raw = fromBase64Url(trimmed);
    if (raw.length === DEK_BYTES) return raw;
  } catch {
    // fall through — may be legacy passphrase
  }
  return null;
};

export const encryptTextV2 = async (plain: string, key: string): Promise<string> => {
  const salt = randomBytes(16);
  const iv = randomBytes(12);
  const aesKey = await deriveAesKey(key, salt);
  const cipher = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: toArrayBuffer(iv) },
    aesKey,
    enc.encode(plain)
  );
  const payload: EncPayloadV1 = {
    v: 1,
    s: toBase64(salt),
    i: toBase64(iv),
    c: toBase64(new Uint8Array(cipher)),
  };
  return `[[enc2]]${btoa(JSON.stringify(payload))}`;
};

export const decryptTextV2 = async (
  content: string | null,
  key: string
): Promise<string | null> => {
  if (!content || !content.startsWith('[[enc2]]') || !key) return null;
  try {
    const payload = JSON.parse(atob(content.slice('[[enc2]]'.length))) as EncPayloadV1;
    if (!payload?.s || !payload?.i || !payload?.c) return null;
    const salt = fromBase64(payload.s);
    const iv = fromBase64(payload.i);
    const cipher = fromBase64(payload.c);
    const aesKey = await deriveAesKey(key, salt);
    const plain = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: toArrayBuffer(iv) },
      aesKey,
      toArrayBuffer(cipher)
    );
    return dec.decode(new Uint8Array(plain));
  } catch {
    return null;
  }
};

/** Per-message DEK encrypt (key = base64url 32-byte DEK). */
export const encryptTextV3 = async (plain: string, dekBase64Url: string): Promise<string> => {
  const dek = parseDek(dekBase64Url);
  if (!dek) throw new Error('Invalid message DEK');
  const iv = randomBytes(12);
  const aesKey = await importRawAesKey(dek);
  const cipher = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: toArrayBuffer(iv) },
    aesKey,
    enc.encode(plain)
  );
  const payload: EncPayloadV3 = {
    v: 3,
    i: toBase64(iv),
    c: toBase64(new Uint8Array(cipher)),
  };
  return `[[enc3]]${btoa(JSON.stringify(payload))}`;
};

export const decryptTextV3 = async (
  content: string | null,
  dekBase64Url: string
): Promise<string | null> => {
  if (!content || !content.startsWith('[[enc3]]') || !dekBase64Url) return null;
  const dek = parseDek(dekBase64Url);
  if (!dek) return null;
  try {
    const payload = JSON.parse(atob(content.slice('[[enc3]]'.length))) as EncPayloadV3;
    if (!payload?.i || !payload?.c) return null;
    const iv = fromBase64(payload.i);
    const cipher = fromBase64(payload.c);
    const aesKey = await importRawAesKey(dek);
    const plain = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: toArrayBuffer(iv) },
      aesKey,
      toArrayBuffer(cipher)
    );
    return dec.decode(new Uint8Array(plain));
  } catch {
    return null;
  }
};

/** Decrypt enc3, else enc2, else null. */
export const decryptTextAny = async (
  content: string | null,
  key: string
): Promise<string | null> => {
  if (!content || !key) return null;
  if (content.startsWith('[[enc3]]')) return decryptTextV3(content, key);
  if (content.startsWith('[[enc2]]')) return decryptTextV2(content, key);
  return null;
};

export const encryptAudioV2 = async (
  bytes: Uint8Array,
  key: string,
  mimeType: string
): Promise<{ content: string; encryptedDataUrl: string }> => {
  const salt = randomBytes(16);
  const iv = randomBytes(12);
  const aesKey = await deriveAesKey(key, salt);
  const cipher = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: toArrayBuffer(iv) },
    aesKey,
    toArrayBuffer(bytes)
  );
  const payload: EncAudioPayloadV1 = {
    v: 1,
    s: toBase64(salt),
    i: toBase64(iv),
    m: mimeType,
  };
  const cipherBase64 = toBase64(new Uint8Array(cipher));
  return {
    content: `[[enc-audio2]]${btoa(JSON.stringify(payload))}`,
    encryptedDataUrl: `data:application/octet-stream;base64,${cipherBase64}`,
  };
};

export const decryptAudioV2 = async (
  content: string | null,
  attachmentUrl: string,
  key: string
): Promise<Blob | null> => {
  if (!content || !content.startsWith('[[enc-audio2]]') || !key) return null;
  try {
    const payload = JSON.parse(
      atob(content.slice('[[enc-audio2]]'.length))
    ) as EncAudioPayloadV1;
    const mimeType = payload?.m || 'audio/webm';
    const salt = fromBase64(payload.s);
    const iv = fromBase64(payload.i);
    const dataMarker = 'base64,';
    const markerIndex = attachmentUrl.indexOf(dataMarker);
    if (markerIndex === -1) return null;
    const cipherBase64 = attachmentUrl.slice(markerIndex + dataMarker.length);
    const cipher = fromBase64(cipherBase64);
    const aesKey = await deriveAesKey(key, salt);
    const plain = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: toArrayBuffer(iv) },
      aesKey,
      toArrayBuffer(cipher)
    );
    return new Blob([plain], { type: mimeType });
  } catch {
    return null;
  }
};

export const encryptAudioV3 = async (
  bytes: Uint8Array,
  dekBase64Url: string,
  mimeType: string
): Promise<{ content: string; encryptedDataUrl: string }> => {
  const dek = parseDek(dekBase64Url);
  if (!dek) throw new Error('Invalid message DEK');
  const iv = randomBytes(12);
  const aesKey = await importRawAesKey(dek);
  const cipher = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: toArrayBuffer(iv) },
    aesKey,
    toArrayBuffer(bytes)
  );
  const payload: EncAudioPayloadV3 = {
    v: 3,
    i: toBase64(iv),
    m: mimeType,
  };
  const cipherBase64 = toBase64(new Uint8Array(cipher));
  return {
    content: `[[enc-audio3]]${btoa(JSON.stringify(payload))}`,
    encryptedDataUrl: `data:application/octet-stream;base64,${cipherBase64}`,
  };
};

export const decryptAudioV3 = async (
  content: string | null,
  attachmentUrl: string,
  dekBase64Url: string
): Promise<Blob | null> => {
  if (!content || !content.startsWith('[[enc-audio3]]') || !dekBase64Url) return null;
  const dek = parseDek(dekBase64Url);
  if (!dek) return null;
  try {
    const payload = JSON.parse(
      atob(content.slice('[[enc-audio3]]'.length))
    ) as EncAudioPayloadV3;
    const mimeType = payload?.m || 'audio/webm';
    const iv = fromBase64(payload.i);
    const dataMarker = 'base64,';
    const markerIndex = attachmentUrl.indexOf(dataMarker);
    if (markerIndex === -1) return null;
    const cipherBase64 = attachmentUrl.slice(markerIndex + dataMarker.length);
    const cipher = fromBase64(cipherBase64);
    const aesKey = await importRawAesKey(dek);
    const plain = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: toArrayBuffer(iv) },
      aesKey,
      toArrayBuffer(cipher)
    );
    return new Blob([plain], { type: mimeType });
  } catch {
    return null;
  }
};

export const decryptAudioAny = async (
  content: string | null,
  attachmentUrl: string,
  key: string
): Promise<Blob | null> => {
  if (!content || !key) return null;
  if (content.startsWith('[[enc-audio3]]')) {
    return decryptAudioV3(content, attachmentUrl, key);
  }
  if (content.startsWith('[[enc-audio2]]')) {
    return decryptAudioV2(content, attachmentUrl, key);
  }
  return null;
};

export const isEncryptedTextContent = (content: string | null | undefined): boolean =>
  !!content &&
  (content.startsWith('[[enc3]]') ||
    content.startsWith('[[enc2]]') ||
    content.startsWith('[[enc]]'));

export const isEncryptedAudioContent = (content: string | null | undefined): boolean =>
  !!content &&
  (content.startsWith('[[enc-audio3]]') || content.startsWith('[[enc-audio2]]'));
